import { Injectable } from '@angular/core';
import {
  Company,
  CompanyResultResponse,
  TagsModel,
  TaxonomySearchResult,
  TeamResultResponse,
  UserAgencyClientModel,
  UserAgencyModel,
  UserValue,
  ValueListsApiService,
} from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { EMPTY, Observable, catchError, combineLatest, lastValueFrom, map, of, shareReplay, tap, throwIfEmpty } from 'rxjs';

type ValueListsCache<T> = Record<string, Observable<T>>;

@Injectable({
  providedIn: 'root',
})
export class ValueListsService {
  private cache: ValueListsCache<
    UserValue[] | CompanyResultResponse | TeamResultResponse | UserAgencyModel[] | UserAgencyClientModel[] | TaxonomySearchResult
  > = {};

  private users = new Map<string, Observable<UserValue>>();
  private companies = new Map<string, Observable<Company>>();
  private tags = new Map<string, Promise<string>>();

  constructor(private readonly apiService: ValueListsApiService) {
    this.listUsers();
    this.listCompanies();
  }

  listUsers(refresh = false): Observable<UserValue[]> {
    if (refresh || !this.cache['users$']) {
      this.cache['users$'] = this.apiService.valueListsControllerUsersList().pipe(
        shareReplay(1),
        tap((users) => {
          users.forEach((user) => {
            this.users.set(user.id, of(user).pipe(shareReplay(1)));
          });
        }),
        catchError(() => {
          delete this.cache['users$'];
          return EMPTY;
        }),
      );
    }
    return this.cache['users$'] as Observable<UserValue[]>;
  }

  lookupUserInfo(userId: AnyType): Observable<UserValue> | undefined {
    if (typeof userId === 'string') {
      let userInfo$ = this.users.get(userId);
      if (!userInfo$) {
        try {
          userInfo$ = this.apiService.valueListsControllerUserInfo(userId).pipe(
            shareReplay(1),
            throwIfEmpty(() => new Error('no user found')),
          );
          this.users.set(userId, userInfo$);
        } catch (err) {
          return undefined;
        }
      }
      return userInfo$;
    }

    if (userId) {
      return of(userId);
    } else {
      return userId;
    }
  }

  lookupUserName(userId: AnyType): Observable<string> | undefined {
    return this.lookupUserInfo(userId)?.pipe(map((user) => user.name));
  }

  listUsersLikeMe(roles: string[] = [], refresh = false, includeSelf = false): Observable<UserValue[]> {
    const rolesString = roles.join(',');
    const cacheKey = 'usersLikeMe$' + rolesString;
    if (refresh || !this.cache[cacheKey]) {
      this.cache[cacheKey] = this.apiService.valueListsControllerUsersList(true, includeSelf, rolesString).pipe(
        shareReplay(1),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<UserValue[]>;
  }

  listCompanies(refresh = false): Observable<CompanyResultResponse> {
    if (refresh || !this.cache['companies$']) {
      this.cache['companies$'] = this.apiService.valueListsControllerCompaniesList().pipe(
        shareReplay(1),
        tap((companyResult) => {
          companyResult.result.forEach((company) => {
            this.companies.set(company._id, of(company).pipe(shareReplay(1)));
          });
        }),
        catchError(() => {
          delete this.cache['companies$'];
          return EMPTY;
        }),
      );
    }
    return this.cache['companies$'] as Observable<CompanyResultResponse>;
  }

  lookupCompanyInfo(companyId: string) {
    let companyInfo$ = this.companies.get(companyId);
    if (!companyInfo$) {
      try {
        companyInfo$ = this.apiService.valueListsControllerCompanyInfo(companyId).pipe(
          shareReplay(1),
          throwIfEmpty(() => new Error('no company found')),
        );
        this.companies.set(companyId, companyInfo$);
      } catch (err) {
        return undefined;
      }
    }
    return companyInfo$;
  }

  lookACB(agency: string, client: string, brand: string) {
    const agencyInfo$ = this.lookupCompanyInfo(agency)?.pipe(map((company) => ({ _id: company._id, name: company.name })));

    const clientInfo$ = this.lookupCompanyInfo(client)?.pipe(
      map((company) => ({
        _id: company._id,
        name: company.name,
        brands: company.brands,
      })),
    );

    const brandInfo$ = clientInfo$?.pipe(
      map((client) => client?.brands?.find((brd) => brd._id === brand)),
      map((brand) => ({ _id: brand?._id, name: brand?.name })),
    );

    return combineLatest([agencyInfo$ || of(undefined), clientInfo$ || of(undefined), brandInfo$ || of(undefined)]).pipe(
      map(([agency, client, brand]) => ({
        agency,
        client,
        brand,
      })),
    );
  }

  listUserAgencies(roles: string[] = [], refresh = false): Observable<UserAgencyModel[]> {
    const rolesString = roles.join(',');
    const cacheKey = 'agencies$' + rolesString;
    if (refresh || !this.cache[cacheKey]) {
      this.cache[cacheKey] = this.apiService.valueListsControllerAgenciesListByUser(rolesString).pipe(
        shareReplay(1),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<UserAgencyModel[]>;
  }

  listUserClients(roles: string[] = [], refresh = false): Observable<UserAgencyClientModel[]> {
    const rolesString = roles.join(',');
    const cacheKey = 'clients$' + rolesString;
    if (refresh || !this.cache[cacheKey]) {
      this.cache[cacheKey] = this.apiService.valueListsControllerListClientsByUser(rolesString).pipe(
        shareReplay(1),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<UserAgencyClientModel[]>;
  }

  listUserByACB(agencyID: string, clientID: string, brandID: string, roles: string[] = [], refresh = false): Observable<UserValue[]> {
    const rolesString = roles.join(',');
    const cacheKey = 'userByACB$.' + agencyID + '.' + clientID + '.' + brandID + '.' + rolesString;
    if (refresh || !this.cache[cacheKey]) {
      this.cache[cacheKey] = this.apiService.valueListsControllerListUsersByACB(agencyID, clientID, brandID, rolesString).pipe(
        shareReplay(1),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<UserValue[]>;
  }

  listMyTeams(): Observable<TeamResultResponse> {
    const cacheKey = 'myTeams$';
    if (!this.cache[cacheKey]) {
      this.cache[cacheKey] = this.apiService.valueListsControllerMyTeams().pipe(
        shareReplay(1),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<TeamResultResponse>;
  }

  listTaxonomies(force?: boolean): Observable<TaxonomySearchResult> {
    const cacheKey = 'taxonomies$';
    if (!this.cache[cacheKey] || force) {
      this.cache[cacheKey] = this.apiService.valueListsControllerTaxonomy().pipe(
        shareReplay(1),
        catchError(() => {
          delete this.cache[cacheKey];
          return EMPTY;
        }),
      );
    }
    return this.cache[cacheKey] as Observable<TaxonomySearchResult>;
  }

  async listTags(tagIds: string[], doNotSort?: boolean): Promise<TagsModel[]> {
    const tagsModel: TagsModel[] = [];
    for await (const tagId of tagIds) {
      const tag = await this.loadCachedTagName(tagId).catch(() => {
        return undefined;
      });
      if (tag) tagsModel.push({ _id: tagId, name: tag });
    }

    if (doNotSort === true) {
      return tagsModel;
    } else {
      return tagsModel.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  lookupTagName(tagId: string): Promise<string | undefined> {
    if (typeof tagId !== 'string') {
      return tagId;
    }
    return this.loadCachedTagName(tagId).catch(() => {
      return undefined;
    });
  }

  private loadCachedTagName(tagId: string): Promise<string> {
    let tag = this.tags.get(tagId);
    if (!tag) {
      tag = lastValueFrom(this.apiService.valueListsControllerTag(tagId).pipe(map((tag) => tag?.name)));
      this.tags.set(tagId, tag);
    }
    return tag;
  }
}
